import { useMemo } from "react";

import {
  faArchive,
  faArrowUpRight,
  faCheck,
  faLink,
  faRotateRight,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import { DisplayDateFromNow } from "src/base-components/Date";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { buildDecisionTraceUrl } from "src/cases/caseView/hooks/buildDecisionTraceUrl";
import { getCaseSourceDecision } from "src/cases/caseView/hooks/getCaseSourceDecision";
import { RunnningCaseAction } from "src/cases/caseView/types";
import { useEagerModeTriggerKey } from "src/cases/caseView/useEagerModeTriggerKey";
import { CaseAssignee } from "src/cases/common/CaseAssignee";
import { useIsCaseCopilotEnabled } from "src/cases/copilot/isCaseCopilotEnabled";
import { useAgentVersionChange } from "src/cases/copilot/useAgentVersionChange";
import { useCaseCopilot } from "src/cases/copilot/useCaseCopilot";
import { getSessionId } from "src/cases/copilot/useCaseCopilotSession";
import { useCopilotSessionStatus } from "src/cases/copilot/useCopilotSessionStatus";
import { useBulkCaseUpdate, useRetryCaseAction } from "src/cases/queries/cases";
import { CaseDeserialized } from "src/cases/queries/casesTypes";
import { CaseTemplateVersionCopilotSettingsOperatingModeEnum } from "src/clients/case-management";
import { useCopilotPrewarm } from "src/copilot/useCopilotPrewarm";
import { CopilotButton } from "src/design-system/CopilotButton/CopilotButton";
import { Pill } from "src/design-system/Pill";
import { Skeleton } from "src/design-system/Skeleton";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useCapabilities } from "src/hooks/useCapabilities";
import { Environment } from "src/hooks/useEnvironment";
import {
  CasePageParams,
  getBaseUrl,
  getUrlToCaseQueuePage,
  getUrlToCaseViewPage,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { logger } from "src/utils/logger";
import { isPreconditionError } from "src/utils/predicates";
import { useParamsDecode } from "src/utils/useParamsDecode";

const CaseActionsDropdown: React.FC<{
  caseCreationActionId?: string;
  caseData?: CaseDeserialized;
}> = ({ caseCreationActionId, caseData }) => {
  const { orgId, wsId, case_id } = useParamsDecode<CasePageParams>();
  const capabilities = useCapabilities();
  const currentUserId = useCurrentUserId();
  const navigate = useNavigate();
  const queueId = caseData?.data.queue?.id;
  const { mutateAsync: bulkUpdateCases } = useBulkCaseUpdate(queueId);
  const { mutateAsync: retryCaseCreationAction } = useRetryCaseAction(case_id);
  const env = caseData?.data.env;
  const caseEtag = caseData?.data.etag ?? 0;

  const canViewDecisionTrace = capabilities.cases.canViewDecisionTrace;
  const sourceDecision = getCaseSourceDecision(caseData);
  const sourceDecisionTraceUrl = buildDecisionTraceUrl({
    orgId,
    wsId,
    flowId: sourceDecision?.flowId,
    versionId: sourceDecision?.flowVersionId,
    decisionId: sourceDecision?.decisionId,
    env,
  });

  // Archive: all queue members can archive, but only in sandbox
  const canArchive = env === "sandbox";

  // Retry case preparation: admin or assigned user
  const canRetryPreparation =
    capabilities.cases.canManage ||
    caseData?.data.assignee?.id === currentUserId;

  const handleShareCaseLink = async () => {
    try {
      const caseUrl = getBaseUrl() + getUrlToCaseViewPage(orgId, wsId, case_id);
      await copyTextToClipboard(caseUrl);
      toastActions.success({ title: "Case link copied to clipboard!" });
    } catch (error) {
      logger.error(`Failed to copy case link: ${error}`);
      toastActions.failure({
        title: "Failed to copy case link",
        description: "Check your browser's clipboard settings",
      });
    }
  };

  const handleArchiveCase = async () => {
    if (!queueId) return;
    try {
      await bulkUpdateCases({
        queueId,
        operation: "archive",
        caseIds: [case_id],
      });
      toastActions.success({ title: "Case archived successfully" });
      navigate(
        getUrlToCaseQueuePage({
          orgId,
          wsId,
          queueId,
          environment: env as Environment,
        }),
      );
    } catch (error) {
      logger.error(`Failed to archive case: ${error}`);
      toastActions.failure({
        title: "Failed to archive case",
        description: "Please try again later",
      });
    }
  };

  const handleRetryCaseCreationAction = async () => {
    if (!caseCreationActionId) {
      logger.warn(`No case preparation flow ID available for retry.`);
      return;
    }

    try {
      await retryCaseCreationAction({
        actionId: caseCreationActionId,
        etag: caseEtag?.toString(),
      });
    } catch (error) {
      logger.error(`Failed to retry case preparation flow: ${error}`);

      if (isPreconditionError(error)) {
        toastActions.failure({
          title: "Failed to retry action",
          description:
            "The case has been modified elsewhere. Please reload and try again.",
        });
        return;
      }

      if (isAxiosError(error)) {
        toastActions.failure({
          title: "Failed to retry case preparation flow",
          description: error.response?.data?.message,
        });
        return;
      }

      toastActions.failure({
        title: "Failed to retry case preparation flow",
        description: "Please try again later",
      });
    }
  };

  const handleViewSourceDecision = () => {
    if (sourceDecisionTraceUrl) {
      window.open(sourceDecisionTraceUrl, "_blank", "noopener,noreferrer");
    }
  };

  // Enable only when a trace URL can actually be built (never a no-op click);
  // stay plainly disabled until the case has loaded.
  const sourceDecisionDisabled = !caseData
    ? true
    : sourceDecisionTraceUrl
      ? undefined
      : "Source decisions are only available for Cases created by a Taktile Decision Flow";

  const elements = [
    {
      icon: faLink,
      key: "Copy Case link",
      action: handleShareCaseLink,
    },
    ...(canViewDecisionTrace
      ? [
          {
            icon: faArrowUpRight,
            key: "View source decision",
            action: handleViewSourceDecision,
            disabled: sourceDecisionDisabled,
          },
        ]
      : []),
    ...(canArchive || (canRetryPreparation && caseCreationActionId)
      ? [
          MENU_DIVIDER,
          ...(canArchive
            ? [
                {
                  icon: faArchive,
                  key: "Archive Case",
                  action: handleArchiveCase,
                  disabled: !queueId,
                },
              ]
            : []),
          ...(canRetryPreparation && caseCreationActionId
            ? [
                {
                  icon: faRotateRight,
                  key: "Retry Case preparation flow",
                  action: handleRetryCaseCreationAction,
                },
              ]
            : []),
        ]
      : []),
  ].filter(Boolean) as OptionsDropdownElement[];

  return (
    <EllipsisOptionsDropdown
      dataLoc="case-actions-dropdown"
      elements={elements}
    />
  );
};

interface CasePageHeaderSideContentProps {
  caseData: CaseDeserialized | undefined;
  isLoading: boolean;
}

export const CasePageHeaderSideContent: React.FC<
  CasePageHeaderSideContentProps
> = ({ caseData, isLoading }) => {
  const { toggleCaseCopilot } = useCaseCopilot();
  const currentUserId = useCurrentUserId();

  const copilotEnabled = useIsCaseCopilotEnabled(caseData);
  const copilotReference = caseData?.data.template_version?.copilot_reference;
  const isEagerMode =
    caseData?.data.template_version?.copilot_settings?.operating_mode ===
    CaseTemplateVersionCopilotSettingsOperatingModeEnum.EAGER;
  const triggerKey = useEagerModeTriggerKey(caseData, currentUserId);

  const caseId = caseData?.data.id ?? "";
  const { activeVersionId } = useAgentVersionChange({
    caseId,
    latestVersionId: copilotReference?.latest_agent_version_id,
  });

  const { isCopilotSessionLoading } = useCopilotSessionStatus({
    userId: currentUserId,
    caseId,
    agentVersionId: activeVersionId,
    enabled: copilotEnabled,
    resetKey: isEagerMode ? triggerKey : 0,
  });

  useCopilotPrewarm({
    agentId: copilotReference?.agent_id,
    sessionId: getSessionId(currentUserId, caseId, activeVersionId),
    enabled: copilotEnabled,
  });

  const failedCaseCreationAction = useMemo(() => {
    const runningAction = caseData?.data?.running_actions?.at(0) as
      | RunnningCaseAction
      | undefined;

    if (
      runningAction?.type === "case_creation" &&
      runningAction?.status === "failed"
    ) {
      return runningAction;
    }
  }, [caseData]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-x-2 pr-3">
        <Skeleton height="h-5.5" width="w-40" />
        <Skeleton height="h-5.5" width="w-40" />
        <CaseActionsDropdown
          caseCreationActionId={failedCaseCreationAction?.id}
          caseData={caseData}
        />
      </div>
    );
  }

  const queueId = caseData?.data.queue?.id;

  return (
    <div className="flex items-center gap-x-2 pr-3">
      {caseData && (
        <>
          <LastUpdatedInfo updatedAt={caseData.data.updated_at} />
          {queueId && (
            <CaseAssignee
              assignee={caseData.data.assignee?.id}
              caseId={caseData.data.id}
              etag={caseData.data.etag}
              queueId={queueId}
            />
          )}
        </>
      )}
      {copilotEnabled && (
        <Tooltip
          disabled={!isCopilotSessionLoading}
          title="Copilot is preparing recommendations"
          asChild
        >
          <CopilotButton
            dataLoc="case-copilot-button"
            isLoading={isCopilotSessionLoading}
            onClick={toggleCaseCopilot}
          >
            Ask Copilot
          </CopilotButton>
        </Tooltip>
      )}
      <CaseActionsDropdown
        caseCreationActionId={failedCaseCreationAction?.id}
        caseData={caseData}
      />
    </div>
  );
};

export const LastUpdatedInfo: React.FC<{
  updatedAt: string | undefined;
}> = ({ updatedAt }) => {
  if (updatedAt) {
    return (
      <Pill size="sm" variant="gray">
        <Pill.Icon icon={faCheck} />

        <Pill.Text>
          Updated <DisplayDateFromNow value={updatedAt} />
        </Pill.Text>
      </Pill>
    );
  }

  return null;
};
